import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('boxit_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('boxit_token');
      localStorage.removeItem('boxit_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
};

// Packages
export const packagesApi = {
  getAll: () => api.get('/packages'),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
};

// Clients
export const clientsApi = {
  getAll: (params = {}) => api.get('/clients', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Domains
export const domainsApi = {
  getByClient: (clientId) => api.get(`/clients/${clientId}/domains`),
  create: (clientId, data) => api.post(`/clients/${clientId}/domains`, data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
};

// KPIs
export const kpisApi = {
  get: () => api.get('/kpis'),
};

// Import
export const importApi = {
  previewClients: (rows) => api.post('/import/clients/preview', { rows }),
  importClients: (data) => api.post('/import/clients', data),
  importDomains: (data) => api.post('/import/domains', data),
};

// GoCardless
export const gocardlessApi = {
  getStatus: () => api.get('/gocardless/status'),
  getCustomers: () => api.get('/gocardless/customers'),
  getSubscriptions: () => api.get('/gocardless/subscriptions'),
  sync: () => api.post('/gocardless/sync'),
};

// Options
export const optionsApi = {
  get: () => api.get('/options'),
};

// Site Content (CMS)
export const siteContentApi = {
  // Examples
  getExamples: () => api.get('/admin/examples'),
  createExample: (data) => api.post('/admin/examples', data),
  updateExample: (id, data) => api.put(`/admin/examples/${id}`, data),
  deleteExample: (id) => api.delete(`/admin/examples/${id}`),
  
  // Packages (marketing)
  getPackages: () => api.get('/admin/packages'),
  createPackage: (data) => api.post('/admin/packages', data),
  updatePackage: (id, data) => api.put(`/admin/packages/${id}`, data),
  deletePackage: (id) => api.delete(`/admin/packages/${id}`),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  createStat: (data) => api.post('/admin/stats', data),
  updateStat: (id, data) => api.put(`/admin/stats/${id}`, data),
  deleteStat: (id) => api.delete(`/admin/stats/${id}`),
};

// Public Site Content (no auth required)
export const publicSiteApi = {
  getExamples: () => api.get('/site/examples'),
  getPackages: () => api.get('/site/packages'),
  getStats: () => api.get('/site/stats'),
};

export default api;
